import {
  Accordion,
  AccordionDetails,
  type AccordionProps,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { ChevronDown, Pencil } from 'lucide-react';

import { iconButtonSx } from '@modules/_shared/components/CardHeaderIconActions';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';

type ListingAccordionProps = Omit<AccordionProps, 'title'> & {
  loading?: boolean;
  disableCollapse?: boolean;
  onEdit?: () => void;
  headerAction?: React.ReactNode;
  disablePadding?: boolean;
  title?: React.ReactNode;
  standalone?: boolean;
};

export const ListingAccordion = ({
  children,
  title,
  loading,
  disableCollapse = false,
  onEdit,
  headerAction,
  disablePadding = false,
  standalone = false,
  sx,
  ...props
}: ListingAccordionProps) => {
  return (
    <Accordion
      variant="outlined"
      elevation={0}
      {...(disableCollapse && { expanded: true, onChange: () => {} })}
      sx={[
        theme => ({
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: (theme.vars ?? theme).palette.grey[300],
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          },
          '& .Mui-disabled': {
            opacity: '1 !important',
            backgroundColor: (theme.vars ?? theme).palette.background.paper,
          },
          '&.MuiAccordion-root.Mui-disabled': {
            backgroundColor: (theme.vars ?? theme).palette.background.paper,
          },
          '&.MuiAccordion-rounded.MuiAccordion-root::before': {
            display: 'none',
          },
          '&.MuiAccordion-root:not(.Mui-expanded)+.MuiAccordion-root:not(.Mui-expanded)':
            {
              borderTopWidth: 0,
            },
          '&.MuiAccordion-root:has(+.MuiAccordion-root.Mui-expanded)': {
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            [theme.breakpoints.down('md')]: {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
          '&.MuiAccordion-root.Mui-expanded+.MuiAccordion-root': {
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            [theme.breakpoints.down('md')]: {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          },
          [theme.breakpoints.down('md')]: {
            borderRadius: '0 !important',
            borderLeftWidth: '0 !important',
            borderRightWidth: '0 !important',
            '&.Mui-expanded': {
              my: 1,
            },
          },
        }),
        standalone &&
          (theme => ({
            borderRadius: '10px !important',
            [theme.breakpoints.down('md')]: {
              borderLeftWidth: '1px !important',
              borderRightWidth: '1px !important',
            },
          })),
        ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
      ]}
      {...props}
    >
      {title && (
        <>
          <AccordionSummary
            expandIcon={disableCollapse ? null : <ChevronDown size={18} />}
            sx={{
              minHeight: 40,
              '&.MuiAccordionSummary-root.Mui-expanded': {
                minHeight: 40,
              },
              '.MuiAccordionSummary-content': {
                my: 1,
              },
              '.MuiAccordionSummary-content.Mui-expanded': {
                my: 1,
              },
              ...(disableCollapse && { cursor: 'default !important' }),
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </AccordionSummary>
          {(headerAction || onEdit) && (
            <Box
              onClick={evt => evt.stopPropagation()}
              sx={{
                position: 'absolute',
                right: disableCollapse ? 12 : 44,
                top: 0,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                zIndex: 1,
              }}
            >
              {headerAction ??
                (onEdit && (
                  <IconButton size="small" onClick={onEdit} sx={iconButtonSx}>
                    <Pencil size={16} />
                  </IconButton>
                ))}
            </Box>
          )}
        </>
      )}
      <AccordionDetails
        sx={{
          display: 'block',
          p: 0,
        }}
      >
        <Divider
          sx={{
            my: 0,
            display: title ? 'block' : 'none',
          }}
        />
        <Box
          sx={{
            p: disablePadding ? 0 : 2,
            position: 'relative',
          }}
        >
          {loading ? <LoadingSpinner /> : children}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
